import { useRef, useEffect, useState } from 'react';

interface NFTShareImageGeneratorProps {
  roi: number; // Return on investment percentage
  rewardSOL: number; // SOL reward amount
  rewardUSD: number; // USD value of reward
  pointsInvested: string; // Points the user invested
  prizeLabel?: string; // Direct prize label from DB (e.g., "1 SOL", "0.75 SOL")
  referralCode: string;
  onImageGenerated: (dataUrl: string) => void;
}

/**
 * Component that generates NFT mint share image using Canvas API
 * Shows lottery performance (ROI, SOL won), not NFT tier data
 */
export function NFTShareImageGenerator({
  roi,
  rewardSOL,
  rewardUSD,
  pointsInvested,
  prizeLabel,
  referralCode,
  onImageGenerated,
}: NFTShareImageGeneratorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'success' | 'error'>('idle');

  useEffect(() => {
    console.log('[NFTShareImageGenerator] Props received:', {
      roi,
      rewardSOL,
      rewardUSD,
      pointsInvested,
      prizeLabel,
      referralCode,
    });

    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match template (1200x1250 with extra height for referral)
    canvas.width = 1200;
    canvas.height = 1250;

    // Determine which template to use based on ROI
    const usePercentageTemplate = roi > 100;

    // Load template image
    const templateImg = new Image();
    templateImg.crossOrigin = 'anonymous';

    // Use the consolidated rewardshare.png template
    const templatePath = '/rewardshare.png';

    console.log('[NFTShareImageGenerator] Loading template:', {
      roi,
      usePercentageTemplate,
      templatePath,
      referralCode: referralCode || '(empty)',
      rewardSOL,
      rewardUSD,
    });

    templateImg.src = templatePath;

    templateImg.onload = () => {
      try {
        console.log('[NFTShareImageGenerator] ✅ Template loaded successfully, generating image...');

        // Draw template as background
        ctx.drawImage(templateImg, 0, 0, canvas.width, canvas.height);

        // Set text rendering quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // DEBUG: Enable to see text placement areas
        const DEBUG_MODE = false;

        if (DEBUG_MODE) {
          // Draw debug rectangles for text areas
          ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
          ctx.lineWidth = 3;
          // Percentage/dollar area (centered, adjusted for new position)
          ctx.strokeRect(0, 570, 960, 250);
          // SOL reward area (adjusted for new position)
          ctx.strokeRect(280, 800, 500, 120);
          // Referral code area (far left, very bottom)
          ctx.strokeRect(50, 1150, 800, 60);
        }

        if (usePercentageTemplate) {
          // Draw percentage (bright green) - centered, moved left
          ctx.fillStyle = '#47FF2E';
          ctx.font = 'bold 120px Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(`+${roi.toFixed(2)}%`, 480, 720);
          console.log('[NFTShareImageGenerator] Drew percentage:', `+${roi.toFixed(2)}%`, 'at (480, 720)');
        } else {
          // Draw dollar amount (bright green) - centered, moved left
          ctx.fillStyle = '#47FF2E';
          ctx.font = 'bold 120px Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(`$${rewardUSD.toFixed(2)}`, 480, 720);
          console.log('[NFTShareImageGenerator] Drew dollar amount:', `$${rewardUSD.toFixed(2)}`, 'at (480, 720)');
        }

        // Draw SOL reward amount - left-aligned, moved left, actual amount from DB
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 60px Arial, sans-serif';
        ctx.textAlign = 'left';
        // Use prize_label from DB if available, otherwise format the amount
        let solText: string;
        if (prizeLabel) {
          solText = prizeLabel; // Use exact label from DB (e.g., "1 SOL")
          console.log('[NFTShareImageGenerator] Using prize_label from DB:', prizeLabel);
        } else {
          // Fallback: Format SOL amount - show integers without decimals, decimals up to 2 places
          solText = rewardSOL % 1 === 0
            ? `${rewardSOL} SOL`
            : `${rewardSOL.toFixed(2)} SOL`;
          console.log('[NFTShareImageGenerator] Calculated SOL text:', solText);
        }
        ctx.fillText(solText, 280, 860);
        console.log('[NFTShareImageGenerator] Drew SOL reward:', solText, 'at (280, 860)');

        // Draw full referral link - moved left, positioned near bottom
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 40px Arial, sans-serif';
        ctx.textAlign = 'left';
        const referralText = referralCode ? `https://www.junk.fun/?ref=${referralCode}` : 'https://www.junk.fun';
        ctx.fillText(referralText, 50, 1185);
        console.log('[NFTShareImageGenerator] Drew referral text:', referralText, 'at (50, 1185)');

        // Convert canvas to data URL
        const dataUrl = canvas.toDataURL('image/png');
        console.log('[NFTShareImageGenerator] ✅ Image generated successfully');
        onImageGenerated(dataUrl);
      } catch (error) {
        console.error('[NFTShareImageGenerator] ❌ Failed to generate NFT share image:', error);
      }
    };

    templateImg.onerror = (error) => {
      console.error('[NFTShareImageGenerator] ❌ Failed to load template image:', templatePath, error);
    };
  }, [roi, rewardSOL, rewardUSD, pointsInvested, prizeLabel, referralCode, onImageGenerated]);

  // Handle clicking on the canvas to copy image
  const handleCanvasClick = async () => {
    if (!canvasRef.current) return;

    setCopyStatus('copying');

    try {
      // Convert canvas to blob
      const blob = await new Promise<Blob | null>((resolve) => {
        canvasRef.current!.toBlob((blob) => resolve(blob), 'image/png');
      });

      if (!blob) {
        throw new Error('Failed to create image blob');
      }

      // Check if Clipboard API is available
      if (navigator.clipboard && window.ClipboardItem) {
        // Use modern Clipboard API
        const item = new ClipboardItem({ 'image/png': blob });
        await navigator.clipboard.write([item]);

        setCopyStatus('success');
        console.log('[NFTShareImageGenerator] ✅ Image copied to clipboard');

        // Reset status after 2 seconds
        setTimeout(() => setCopyStatus('idle'), 2000);
      } else {
        // Fallback: Download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'junk-fun-rewards.png';
        a.click();
        URL.revokeObjectURL(url);

        setCopyStatus('success');
        console.log('[NFTShareImageGenerator] ✅ Image downloaded (clipboard not supported)');
        setTimeout(() => setCopyStatus('idle'), 2000);
      }
    } catch (error) {
      console.error('[NFTShareImageGenerator] ❌ Failed to copy image:', error);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 3000);
    }
  };

  // DEBUG: Change to visible for debugging, use "hidden" for production
  const DEBUG_MODE = false;

  return (
    <div className={DEBUG_MODE ? "border-4 border-red-500 p-4 max-w-2xl mx-auto" : "relative"}>
      {DEBUG_MODE && <p className="text-white mb-2">DEBUG: Share Image Canvas (remove this in production)</p>}

      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-auto cursor-pointer transition-opacity hover:opacity-90"
          onClick={handleCanvasClick}
          title="Click to copy image"
        />

        {/* Copy status overlay */}
        {copyStatus !== 'idle' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 pointer-events-none">
            <div className={`px-4 py-2 rounded-lg font-bold text-white ${
              copyStatus === 'success' ? 'bg-green-500' :
              copyStatus === 'error' ? 'bg-red-500' :
              'bg-blue-500'
            }`}>
              {copyStatus === 'copying' && 'Copying image...'}
              {copyStatus === 'success' && '✓ Image copied to clipboard!'}
              {copyStatus === 'error' && '✗ Failed to copy image'}
            </div>
          </div>
        )}
      </div>

      <p className="text-center text-sm text-gray-400 mt-2">
        Click the image to copy it to your clipboard
      </p>
    </div>
  );
}
